import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  updateDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp,
  deleteDoc,
  writeBatch,
  getDoc,
  addDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Position, Transaction } from '../types';
import { differenceInDays, startOfDay } from 'date-fns';

export class PositionService {
  private static POSITIONS_COLLECTION = 'positions';
  private static TRANSACTIONS_COLLECTION = 'transactions';

  static async openPosition(
    portfolioId: string,
    symbol: string,
    quantity: number,
    price: number,
    stopLoss: number,
    targetPrice: number,
    orderType: 'market' | 'limit' = 'market',
    status: 'pending' | 'open' = 'open',
    entryDate?: Date
  ): Promise<Position> {
    const docRef = doc(collection(db, this.POSITIONS_COLLECTION));
    const position: Omit<Position, 'id'> = {
      portfolioId,
      symbol,
      quantity,
      entryPrice: price,
      entryDate: entryDate || new Date(),
      currentPrice: price,
      stopLoss,
      targetPrice,
      status,
      orderType,
      unrealizedGainLoss: 0,
      holdingDays: 0
    };

    // Only add limitPrice if it's a limit order
    if (orderType === 'limit' && status === 'pending') {
      position.limitPrice = price;
    }
    
    await setDoc(docRef, {
      ...position,
      entryDate: entryDate || serverTimestamp()
    });
    
    // Only record transaction if the position is open (not pending)
    if (status === 'open') {
      await this.recordTransaction(portfolioId, symbol, 'buy', quantity, price);
    }
    
    return { ...position, id: docRef.id } as Position;
  }

  static async closePosition(
    positionId: string,
    exitPrice: number,
    exitDate?: Date
  ): Promise<void> {
    const positionRef = doc(db, this.POSITIONS_COLLECTION, positionId);
    
    // Get position details first
    const position = await this.getPosition(positionId);
    if (!position) {
      throw new Error('Position not found');
    }
    
    // Calculate gain/loss
    const totalProceeds = exitPrice * position.quantity;
    const totalCost = position.entryPrice * position.quantity;
    const realizedGainLoss = totalProceeds - totalCost;
    
    // Update position to closed
    await updateDoc(positionRef, {
      status: 'closed',
      exitPrice,
      exitDate: exitDate || serverTimestamp(),
      currentPrice: exitPrice,
      realizedGainLoss
    });
    
    // Record sell transaction
    await this.recordTransaction(
      position.portfolioId,
      position.symbol,
      'sell',
      position.quantity,
      exitPrice
    );
    
    // Update portfolio funds and earnings
    const { PortfolioService } = await import('./portfolioService');
    const portfolio = await PortfolioService.getPortfolio(position.portfolioId);
    if (portfolio) {
      // Recalculate totalEarnings from ALL closed positions (source of truth)
      // The position was already updated to "closed" above, so getClosedPositions includes it
      const closedPositions = await this.getClosedPositions(position.portfolioId);
      const newTotalEarnings = closedPositions.reduce(
        (sum, p) => sum + (p.realizedGainLoss || 0), 0
      );

      // Recalculate available funds based on: initial fund - open positions cost + realized losses
      const openPositions = await this.getOpenPositions(position.portfolioId);
      const openPositionsCost = openPositions
        .filter(p => p.id !== positionId) // Exclude the position we're closing
        .reduce((sum, p) => sum + (p.entryPrice * p.quantity), 0);

      // Only subtract losses from available funds, don't add gains
      const realizedLosses = Math.min(0, newTotalEarnings);
      const newAvailableFunds = portfolio.initialFundValue - openPositionsCost + realizedLosses;

      await PortfolioService.updatePortfolioAfterClose(
        position.portfolioId,
        newAvailableFunds,
        newTotalEarnings
      );
    }
  }

  static async executePendingPosition(
    positionId: string,
    executionPrice: number,
    executionDate: Date
  ): Promise<void> {
    const positionRef = doc(db, this.POSITIONS_COLLECTION, positionId);
    
    // Get position details first
    const position = await this.getPosition(positionId);
    if (!position || position.status !== 'pending') {
      throw new Error('Position not found or not pending');
    }
    
    // Update position to open status
    await updateDoc(positionRef, {
      status: 'open',
      entryPrice: executionPrice,
      currentPrice: executionPrice,
      entryDate: executionDate
    });
    
    // Record the buy transaction
    await this.recordTransaction(
      position.portfolioId,
      position.symbol,
      'buy',
      position.quantity,
      executionPrice
    );
    
    // Update portfolio available funds (difference between limit and execution price if any)
    const priceDifference = (position.limitPrice || executionPrice) - executionPrice;
    if (priceDifference !== 0) {
      const { PortfolioService } = await import('./portfolioService');
      const portfolio = await PortfolioService.getPortfolio(position.portfolioId);
      if (portfolio) {
        const adjustment = priceDifference * position.quantity;
        await PortfolioService.updateAvailableFunds(
          position.portfolioId,
          portfolio.availableFunds + adjustment
        );
      }
    }
  }

  static async cancelPendingPosition(positionId: string): Promise<void> {
    const positionRef = doc(db, this.POSITIONS_COLLECTION, positionId);
    
    // Get position details first
    const position = await this.getPosition(positionId);
    if (!position || position.status !== 'pending') {
      throw new Error('Position not found or not pending');
    }
    
    // Update position status to cancelled
    await updateDoc(positionRef, {
      status: 'cancelled',
      updatedAt: serverTimestamp()
    });
    
    // Refund the allocated funds back to available funds
    const allocatedFunds = (position.limitPrice || position.entryPrice) * position.quantity;
    const { PortfolioService } = await import('./portfolioService');
    const portfolio = await PortfolioService.getPortfolio(position.portfolioId);
    if (portfolio) {
      await PortfolioService.updateAvailableFunds(
        position.portfolioId,
        portfolio.availableFunds + allocatedFunds
      );
    }
  }

  static async getOpenPositions(portfolioId: string): Promise<Position[]> {
    const q = query(
      collection(db, this.POSITIONS_COLLECTION),
      where('portfolioId', '==', portfolioId),
      where('status', '==', 'open'),
      orderBy('entryDate', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const positions: Position[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const position = {
        ...data,
        id: doc.id,
        entryDate: data.entryDate?.toDate(),
        exitDate: data.exitDate?.toDate()
      } as Position;


      // Calculate metrics
      position.unrealizedGainLoss = this.calculateUnrealizedGainLoss(position);
      // Pending positions have 0 holding days
      position.holdingDays = position.status === 'pending' ? 0 : this.calculateHoldingDays(position.entryDate);
      position.realizedGainLoss = position.status === 'closed' ? this.calculateRealizedGainLoss(position) : undefined;

      positions.push(position);
    });

    return positions;
  }

  /**
   * Get all active positions (open + pending) for a portfolio
   * Used for capacity calculation in recommendation engine
   * Pending positions (limit orders) are considered allocated even though not yet executed
   */
  static async getActivePositions(portfolioId: string): Promise<Position[]> {
    const q = query(
      collection(db, this.POSITIONS_COLLECTION),
      where('portfolioId', '==', portfolioId),
      where('status', 'in', ['open', 'pending']),
      orderBy('entryDate', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const positions: Position[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const position = {
        ...data,
        id: doc.id,
        entryDate: data.entryDate?.toDate(),
        exitDate: data.exitDate?.toDate()
      } as Position;

      // Calculate metrics
      position.unrealizedGainLoss = this.calculateUnrealizedGainLoss(position);
      // Pending positions have 0 holding days
      position.holdingDays = position.status === 'pending' ? 0 : this.calculateHoldingDays(position.entryDate);
      position.realizedGainLoss = position.status === 'closed' ? this.calculateRealizedGainLoss(position) : undefined;

      positions.push(position);
    });

    return positions;
  }

  static async getPosition(positionId: string): Promise<Position | null> {
    const docRef = doc(db, this.POSITIONS_COLLECTION, positionId);
    const docSnap = await getDocs(query(collection(db, this.POSITIONS_COLLECTION), where('__name__', '==', positionId)));
    
    if (!docSnap.empty) {
      const doc = docSnap.docs[0];
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        entryDate: data.entryDate?.toDate(),
        exitDate: data.exitDate?.toDate()
      } as Position;
    }
    
    return null;
  }

  static async updatePositionPrices(positions: { positionId: string; currentPrice: number }[]): Promise<void> {
    const updatePromises = positions.map(({ positionId, currentPrice }) => {
      const positionRef = doc(db, this.POSITIONS_COLLECTION, positionId);
      return updateDoc(positionRef, { currentPrice });
    });
    
    await Promise.all(updatePromises);
  }

  private static calculateUnrealizedGainLoss(position: Position): number {
    // Pending positions have no unrealized gains/losses
    if (position.status === 'pending') {
      return 0;
    }
    return (position.currentPrice - position.entryPrice) * position.quantity;
  }

  private static calculateRealizedGainLoss(position: Position): number {
    if (position.exitPrice) {
      return (position.exitPrice - position.entryPrice) * position.quantity;
    }
    return 0;
  }

  private static calculateHoldingDays(entryDate: Date): number {
    // Use startOfDay to ensure we're comparing full days, not time differences
    const entryDay = startOfDay(entryDate);
    const today = startOfDay(new Date());
    return differenceInDays(today, entryDay);
  }

  private static async recordTransaction(
    portfolioId: string,
    symbol: string,
    type: 'buy' | 'sell',
    quantity: number,
    price: number
  ): Promise<void> {
    const docRef = doc(collection(db, this.TRANSACTIONS_COLLECTION));
    const transaction: Omit<Transaction, 'id'> = {
      portfolioId,
      symbol,
      type,
      quantity,
      price,
      totalAmount: quantity * price,
      executedAt: new Date()
    };
    
    await setDoc(docRef, {
      ...transaction,
      executedAt: serverTimestamp()
    });
  }

  static async getPositionsNearStopLoss(portfolioId: string, threshold: number = 5): Promise<Position[]> {
    const openPositions = await this.getOpenPositions(portfolioId);
    
    return openPositions.filter(position => {
      const percentFromStop = ((position.currentPrice - position.stopLoss) / position.stopLoss) * 100;
      return percentFromStop <= threshold;
    });
  }

  static async getPositionsHeldTooLong(portfolioId: string, maxDays: number = 28): Promise<Position[]> {
    const openPositions = await this.getOpenPositions(portfolioId);
    return openPositions.filter(position => position.holdingDays >= maxDays);
  }

  static async updateStopLoss(positionId: string, newStopLoss: number): Promise<void> {
    try {
      const docRef = doc(db, this.POSITIONS_COLLECTION, positionId);
      await updateDoc(docRef, {
        stopLoss: newStopLoss,
        updatedAt: serverTimestamp()
      });
      console.log(`Updated stop loss for position ${positionId} to ${newStopLoss}`);
    } catch (error) {
      console.error('Error updating stop loss:', error);
      throw error;
    }
  }

  static async updateTargetPrice(positionId: string, newTargetPrice: number): Promise<void> {
    try {
      const docRef = doc(db, this.POSITIONS_COLLECTION, positionId);
      await updateDoc(docRef, {
        targetPrice: newTargetPrice,
        updatedAt: serverTimestamp()
      });
      console.log(`Updated target price for position ${positionId} to ${newTargetPrice}`);
    } catch (error) {
      console.error('Error updating target price:', error);
      throw error;
    }
  }

  static async deletePosition(positionId: string): Promise<void> {
    try {
      // Get the position details first
      const position = await this.getPosition(positionId);
      if (!position) {
        throw new Error('Position not found');
      }

      // Delete the position
      const positionRef = doc(db, this.POSITIONS_COLLECTION, positionId);
      await deleteDoc(positionRef);

      // Delete all related transactions
      const transactionsQuery = query(
        collection(db, this.TRANSACTIONS_COLLECTION),
        where('portfolioId', '==', position.portfolioId),
        where('symbol', '==', position.symbol)
      );
      const transactionSnapshot = await getDocs(transactionsQuery);
      
      const batch = writeBatch(db);
      transactionSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      // Update portfolio values
      await this.updateAffectedPortfolioValues([position.portfolioId]);
      
      console.log(`Deleted position ${positionId} and related transactions`);
    } catch (error) {
      console.error('Error deleting position:', error);
      throw error;
    }
  }

  static async getPendingPositions(portfolioId: string): Promise<Position[]> {
    const q = query(
      collection(db, this.POSITIONS_COLLECTION),
      where('portfolioId', '==', portfolioId),
      where('status', '==', 'pending'),
      orderBy('entryDate', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const positions: Position[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const position = {
        ...data,
        id: doc.id,
        entryDate: data.entryDate?.toDate(),
        exitDate: data.exitDate?.toDate()
      } as Position;
      
      // Pending positions have 0 holding days and no unrealized gains
      position.holdingDays = 0;
      position.unrealizedGainLoss = 0;
      
      positions.push(position);
    });
    
    return positions;
  }

  static async getAllOpenPositions(): Promise<Position[]> {
    const q = query(
      collection(db, this.POSITIONS_COLLECTION),
      where('status', '==', 'open'),
      orderBy('entryDate', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const positions: Position[] = [];
    
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const position = {
        ...data,
        id: doc.id,
        entryDate: data.entryDate?.toDate(),
        exitDate: data.exitDate?.toDate()
      } as Position;
      
      
      // Calculate metrics
      position.unrealizedGainLoss = this.calculateUnrealizedGainLoss(position);
      // Pending positions have 0 holding days
      position.holdingDays = position.status === 'pending' ? 0 : this.calculateHoldingDays(position.entryDate);
      position.realizedGainLoss = position.status === 'closed' ? this.calculateRealizedGainLoss(position) : undefined;
      
      positions.push(position);
    });
    
    return positions;
  }

  static async updatePositionPricesBySymbol(symbolPrices: { symbol: string; currentPrice: number }[]): Promise<void> {
    
    // Get all open AND pending positions (pending positions need current price for limit percentage)
    const [openPositions, allPendingPositions] = await Promise.all([
      this.getAllOpenPositions(),
      getDocs(query(
        collection(db, this.POSITIONS_COLLECTION),
        where('status', '==', 'pending')
      ))
    ]);
    
    // Combine open and pending positions
    const pendingPositions: Position[] = [];
    allPendingPositions.forEach((doc) => {
      const data = doc.data();
      pendingPositions.push({
        ...data,
        id: doc.id,
        entryDate: data.entryDate?.toDate(),
        exitDate: data.exitDate?.toDate()
      } as Position);
    });
    
    const allPositions = [...openPositions, ...pendingPositions];
    
    // Create a map of symbol to price for quick lookup
    const priceMap = new Map(symbolPrices.map(sp => [sp.symbol, sp.currentPrice]));
    
    // Find positions that need price updates
    const positionUpdates: { positionId: string; currentPrice: number }[] = [];
    const affectedPortfolios = new Set<string>();
    
    allPositions.forEach(position => {
      const newPrice = priceMap.get(position.symbol);
      if (newPrice !== undefined && newPrice !== position.currentPrice) {
        positionUpdates.push({
          positionId: position.id,
          currentPrice: newPrice
        });
        affectedPortfolios.add(position.portfolioId);
      }
    });
    
    
    // Update positions if any need updating
    console.log(`Found ${positionUpdates.length} positions to update prices`);
    if (positionUpdates.length > 0) {
      console.log('Position updates:', positionUpdates);
      await this.updatePositionPrices(positionUpdates);
      
      // Update portfolio values for affected portfolios
      await this.updateAffectedPortfolioValues(Array.from(affectedPortfolios));
      console.log(`Updated ${positionUpdates.length} position prices`);
    } else {
      console.log('No positions found needing price updates');
    }
  }

  static async getPortfolioTransactions(portfolioId: string): Promise<Transaction[]> {
    const q = query(
      collection(db, this.TRANSACTIONS_COLLECTION),
      where('portfolioId', '==', portfolioId),
      orderBy('executedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const transactions: Transaction[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      transactions.push({
        ...data,
        id: doc.id,
        executedAt: data.executedAt?.toDate()
      } as Transaction);
    });
    
    return transactions;
  }

  static async updatePositionTargets(
    positionId: string,
    stopLoss: number,
    targetPrice: number
  ): Promise<void> {
    const positionRef = doc(db, this.POSITIONS_COLLECTION, positionId);
    
    await updateDoc(positionRef, {
      stopLoss,
      targetPrice,
      updatedAt: serverTimestamp()
    });
  }

  static async updatePositionDetails(
    positionId: string,
    stopLoss: number,
    targetPrice: number,
    entryPrice: number,
    entryDate: Date,
    quantity?: number
  ): Promise<void> {
    const positionRef = doc(db, this.POSITIONS_COLLECTION, positionId);
    
    // Get the current position to check if we need to update transactions
    const position = await this.getPosition(positionId);
    if (!position) {
      throw new Error('Position not found');
    }
    
    // Update position with new details
    const updateData: any = {
      stopLoss,
      targetPrice,
      entryPrice,
      entryDate,
      updatedAt: serverTimestamp()
    };
    
    if (quantity !== undefined) {
      updateData.quantity = quantity;
    }
    
    await updateDoc(positionRef, updateData);
    
    // If entry price or quantity changed, we need to update the buy transaction
    const finalQuantity = quantity !== undefined ? quantity : position.quantity;
    if (position.entryPrice !== entryPrice || position.quantity !== finalQuantity) {
      // Find and update the buy transaction for this position
      const transactionsQuery = query(
        collection(db, this.TRANSACTIONS_COLLECTION),
        where('portfolioId', '==', position.portfolioId),
        where('symbol', '==', position.symbol),
        where('type', '==', 'buy'),
        orderBy('executedAt', 'desc')
      );
      
      const transactionSnapshot = await getDocs(transactionsQuery);
      
      // Update the most recent buy transaction that matches the original quantity and price
      for (const doc of transactionSnapshot.docs) {
        const transaction = doc.data();
        if (transaction.quantity === position.quantity && 
            Math.abs(transaction.price - position.entryPrice) < 0.01) {
          await updateDoc(doc.ref, {
            price: entryPrice,
            quantity: finalQuantity,
            totalAmount: finalQuantity * entryPrice,
            executedAt: entryDate
          });
          break;
        }
      }
      
      // Update portfolio values if entry price or quantity changed
      const oldTotalCost = position.entryPrice * position.quantity;
      const newTotalCost = entryPrice * finalQuantity;
      const costDifference = oldTotalCost - newTotalCost;
      
      if (costDifference !== 0) {
        const { PortfolioService } = await import('./portfolioService');
        const portfolio = await PortfolioService.getPortfolio(position.portfolioId);
        if (portfolio) {
          // Adjust available funds based on total cost difference
          await PortfolioService.updateAvailableFunds(
            position.portfolioId,
            portfolio.availableFunds + costDifference
          );
        }
      }
    }
  }

  static async getClosedPositions(portfolioId: string): Promise<Position[]> {
    const q = query(
      collection(db, this.POSITIONS_COLLECTION),
      where('portfolioId', '==', portfolioId),
      where('status', '==', 'closed'),
      orderBy('exitDate', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const positions: Position[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const position = {
        ...data,
        id: doc.id,
        entryDate: data.entryDate?.toDate(),
        exitDate: data.exitDate?.toDate()
      } as Position;
      
      positions.push(position);
    });
    
    return positions;
  }

  private static async updateAffectedPortfolioValues(portfolioIds: string[]): Promise<void> {
    const { PortfolioService } = await import('./portfolioService');
    
    const updatePromises = portfolioIds.map(async (portfolioId) => {
      try {
        const portfolio = await PortfolioService.getPortfolio(portfolioId);
        if (!portfolio) {
            return;
        }
        
        const positions = await this.getOpenPositions(portfolioId);
        
        const metrics = PortfolioService.calculatePortfolioMetrics(portfolio, positions);
        
        await PortfolioService.updatePortfolioValue(
          portfolioId,
          metrics.totalValue,
          portfolio.availableFunds
        );
      } catch (error) {
        console.error(`Error updating portfolio ${portfolioId} value:`, error);
      }
    });
    
    await Promise.all(updatePromises);
  }

  /**
   * Check if a stock has any position history (open, closed, or pending) for a user
   */
  static async hasPositionHistory(symbol: string, userId: string): Promise<boolean> {
    // Get all portfolios for the user
    const { PortfolioService } = await import('./portfolioService');
    const userPortfolios = await PortfolioService.getUserPortfolios();
    const portfolioIds = userPortfolios.map(p => p.id);
    
    if (portfolioIds.length === 0) {
      return false;
    }
    
    // Check for any positions with this symbol in user's portfolios
    const q = query(
      collection(db, this.POSITIONS_COLLECTION),
      where('portfolioId', 'in', portfolioIds),
      where('symbol', '==', symbol)
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  }
}